import { Environment } from '@provider-directory/services/getEnvironmentVariables';

interface BaseWhiteplainsMenuItem {
  text: string;
  href: string;
  isExternal?: boolean;
  isInternal?: boolean;
  className?: string;
}

export interface ParentWhiteplainsMenuItem extends BaseWhiteplainsMenuItem {
  children: WhiteplainsMenuItem[];
  ariaControls: string;
  id: string;
  label?: string;
}

interface LeafWhiteplainsMenuItem extends BaseWhiteplainsMenuItem {
  children?: never;
}

export type WhiteplainsMenuItem =
  | LeafWhiteplainsMenuItem
  | ParentWhiteplainsMenuItem;

export const getHeaderNavigationItems = (
  env?: string,
): WhiteplainsMenuItem[] => {
  const isProd = env === Environment.PROD;
  return [
    {
      text: 'About Us',
      href: 'javascript:;',
      id: 'uidaaklaia0z',
      ariaControls: 'uidplt0c6mnm',
      label: 'Show submenu for About Us',
      children: [
        {
          text: 'Accreditations and Awards',
          href: '/about-us/accreditations-and-awards/',
        },
        {
          text: 'Annual Report',
          href: 'https://online.fliphtml5.com/symyi/ovdu/#p=1',
          isExternal: true,
        },
        {
          text: 'Campus Transformation',
          href: '/about-us/campus-transformation/',
        },
        {
          text: 'Careers',
          href: '/about-us/careers/',
          ariaControls: 'uidxpb0vpabf',
          id: 'uidadrb9mjwd',
          children: [
            {
              text: 'Physician Career Opportunities',
              href: '/about-us/careers/physician-career-opportunities/',
            },
          ],
        },
        {
          text: 'Leadership',
          href: '/about-us/leadership/',
          ariaControls: 'uidc9s4couaa',
          id: 'uidlm9x4shrb',
          children: [
            {
              text: 'Board of Directors',
              href: '/about-us/leadership/board-of-directors/',
            },
          ],
        },
        {
          text: 'Marketing Campaign',
          href: '/about-us/marketing-campaign/',
        },
        {
          text: 'News Center',
          href: 'https://healthmatters.wphospital.org/news?s=&search_category=&content_type=news',
          isExternal: true,
        },
        {
          text: 'Nursing Excellence',
          href: '/about-us/nursing-excellence/',
        },
        {
          text: 'Quality Measures',
          href: '/about-us/quality-measures/',
        },
        {
          text: 'Volunteer Opportunities',
          href: '/about-us/volunteer-opportunities/',
        },
        {
          text: 'Who We Are',
          href: '/about-us/who-we-are/',
        },
      ],
    },
    {
      text: 'Patients & Visitors',
      href: 'javascript:;',
      ariaControls: 'uidxfykldnpa',
      id: 'uid2dfvr28b0',
      label: 'Show submenu for Patients & Visitors',
      className: 'c-nav-primary__pv parent js-dropdown-toggle',
      children: [
        {
          text: 'Patients',
          href: 'javascript:;',
          ariaControls: 'uidciv5bmx6w',
          id: 'uidtqruteqbu',
          className: 'c-nav-primary__pv parent js-dropdown-toggle',
          children: [
            {
              text: 'Advance Directives',
              href: '/patients-visitors/patients/advance-directives/',
            },
            {
              text: 'Billing Information',
              href: '/patients-visitors/patients/billing-information/',
              ariaControls: 'uidu0j1mxyrz',
              id: 'uidh5sesq4m7',
              children: [
                {
                  text: 'Standard Charges',
                  href: '/patients-visitors/patients/billing-information/standard-charges/',
                },
                {
                  text: 'Request a Cost Estimate',
                  href: '/forms/request-a-cost-estimate/',
                },
              ],
            },
            {
              text: 'COVID-19 Resources',
              href: '/patients-visitors/patients/covid-19-resources/',
            },
            {
              text: 'DAISY Award Nurse Nomination',
              href: '/forms/daisy-award-nurse-nomination/',
            },
            {
              text: 'Food Services',
              href: '/patients-visitors/patients/food-services/',
            },
            {
              text: 'Medical Records',
              href: '/patients-visitors/patients/medical-records/',
            },
            {
              text: 'Patient Information',
              href: '/patients-visitors/patients/patient-information/',
            },
            {
              text: 'Patient Portal',
              href: '/patients-visitors/patients/patient-portal/',
            },
            {
              text: 'Patient Relations',
              href: '/patients-visitors/patients/patient-relations/',
            },
            {
              text: 'Patient Rights and Responsibilities',
              href: '/patients-visitors/patients/patient-rights-and-responsibilities/',
            },
            {
              text: 'Patient Stories',
              href: '/patient-stories/',
              ariaControls: 'uid6b9pza06y',
              id: 'uid56id4wms6',
              children: [
                {
                  text: 'Send Us Your Story',
                  href: '/forms/send-us-your-story/',
                },
              ],
            },
            {
              text: 'Remote Patient Monitoring',
              href: '/patients-visitors/patients/remote-patient-monitoring/',
            },
            {
              text: 'Spiritual Care',
              href: '/patients-visitors/patients/spiritual-care/',
            },
            {
              text: 'TULIP Award Nurse Technician Nomination',
              href: '/forms/tulip-award-nurse-technician-nomination/',
            },
          ],
        },
        {
          text: 'Visitors',
          href: 'javascript:;',
          ariaControls: 'uidwrho6ifty',
          id: 'uid8qdf79qra',
          className: 'parent js-dropdown-toggle',
          children: [
            {
              text: 'Cheer Cards',
              href: '/cheer-cards/',
            },
            {
              text: 'Everyday Healthy Café',
              href: '/patients-visitors/visitors/everyday-healthy-café/',
            },
            {
              text: 'Gift Shop',
              href: '/patients-visitors/visitors/gift-shop/',
            },
            {
              text: 'Visitor Information',
              href: '/patients-visitors/visitors/visitor-information/',
              ariaControls: 'uidp1207yifd',
              id: 'uidrsa8k4tsl',
              children: [
                {
                  text: 'Hotels',
                  href: '/patients-visitors/visitors/visitor-information/hotels/',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      text: 'Find a Provider',
      // TODO: Remove location filter when WPH adds more slots to their providers
      // WPH does not want to show MTF providers first when you click this link. To do this, they have asked us to add location filtering
      href: isProd
        ? 'https://doctors.wphospital.org/providers/find?address=White+Plains%2C+NY%2C+USA&center-point=41.0332209%2C-73.7650415&max-distance=10'
        : '/providers/find?address=White+Plains%2C+NY%2C+USA&center-point=41.0332209%2C-73.7650415&max-distance=10',
      isInternal: !isProd,
    },
    {
      text: 'Services',
      href: '/services/',
      ariaControls: 'uidyjrzls6aq',
      id: 'uidyrdvad4xs',
      label: 'Show submenu for Services',
      className: 'c-nav-primary__services parent js-dropdown-toggle',
      children: [
        {
          text: 'Bariatric surgery',
          href: '/services/bariatric-surgery/',
          ariaControls: 'uids6d9m9i3c',
          id: 'uidblmgikmy1',
          className: 'parent js-dropdown-toggle',
          children: [
            {
              text: 'Seminars',
              href: '/services/bariatric-surgery/seminars/',
            },
            {
              text: 'Surgical options',
              href: '/services/bariatric-surgery/surgical-options/',
            },
          ],
        },
        {
          text: 'Behavioral health services',
          href: '/services/behavioral-health-services/',
        },
        {
          text: 'Cancer care',
          href: '/services/cancer-care/',
          ariaControls: 'uidty80tynhf',
          id: 'uidsx9gb7oxi',
          className: 'parent js-dropdown-toggle',
          children: [
            {
              text: 'Cancer clinical trials',
              href: '/services/cancer-care/cancer-clinical-trials/',
            },
            {
              text: 'Cancer treatments',
              href: '/services/cancer-care/cancer-treatments/',
              ariaControls: 'uid10wc1eer2',
              id: 'uidm6qi7pu11',
              className: 'parent js-dropdown-toggle',
              children: [
                {
                  text: 'Interventional oncology',
                  href: '/services/cancer-care/cancer-treatments/interventional-oncology/',
                },
                {
                  text: 'Radiation oncology',
                  href: '/services/cancer-care/cancer-treatments/radiation-oncology/',
                },
              ],
            },
            {
              text: 'Cancers we treat',
              href: 'javascript:;',
              ariaControls: 'uidtf6tlr9y8',
              id: 'uid4ahibj1yf',
              className: 'parent js-dropdown-toggle',
              children: [
                {
                  text: 'Brain cancer',
                  href: '/services/cancer-care/cancers-we-treat/brain-cancer/',
                },
                {
                  text: 'Breast cancer',
                  href: '/services/cancer-care/cancers-we-treat/breast-cancer/',
                },
                {
                  text: 'Blood cancer',
                  href: '/services/cancer-care/cancers-we-treat/blood-cancer/',
                },
                {
                  text: 'Gastrointestinal cancer',
                  href: '/services/cancer-care/cancers-we-treat/gastrointestinal-cancer/',
                },
                {
                  text: 'Gynecological cancer',
                  href: '/services/cancer-care/cancers-we-treat/gynecological-cancer/',
                },
                {
                  text: 'Head and neck cancer',
                  href: '/services/cancer-care/cancers-we-treat/head-and-neck-cancer/',
                },
                {
                  text: 'Liver cancer',
                  href: '/services/gastroenterology/hepatobiliary-surgery/',
                },
                {
                  text: 'Lung and thoracic cancer',
                  href: '/services/cancer-care/cancers-we-treat/lung-and-thoracic-cancer/',
                },
                {
                  text: 'Lymphoma',
                  href: '/services/cancer-care/cancers-we-treat/lymphoma/',
                },
                {
                  text: 'Orthopedic oncology',
                  href: '/services/cancer-care/cancers-we-treat/orthopedic-oncology/',
                },
                {
                  text: 'Pancreatic cancer',
                  href: '/services/cancer-care/cancers-we-treat/pancreatic-cancer/',
                },
                {
                  text: 'Skin cancer',
                  href: '/services/cancer-care/cancers-we-treat/skin-cancer/',
                },
                {
                  text: 'Thymoma',
                  href: '/services/cancer-care/cancers-we-treat/thymoma/',
                },
                {
                  text: 'Thyroid cancer',
                  href: '/services/cancer-care/cancers-we-treat/thyroid-cancer/',
                },
                {
                  text: 'Urologic cancer',
                  href: '/services/cancer-care/cancers-we-treat/urologic-cancer/',
                },
              ],
            },
          ],
        },
        {
          href: '/services/cancer-care/screenings/',
          text: 'Screenings',
        },
        {
          href: '/services/cancer-care/support-services/',
          text: 'Support services',
        },
        {
          href: '/services/cancer-care/supportive-services-staff/',
          text: 'Supportive services staff',
        },
        {
          href: '/services/cancer-care/survivorship-program/',
          text: 'Survivorship program',
          ariaControls: 'uidw950tsyng',
          id: 'uidkmvjwxlik',
          children: [
            {
              href: '/services/cancer-care/survivorship-program/virtual-survivorship-services/',
              text: 'Virtual survivorship services',
            },
          ],
        },
        {
          href: '/services/cancer-care/tumor-boards/',
          text: 'Tumor boards',
        },
        {
          href: '/services/cancer-care/the-young-womens-program-for-cancer-care/',
          text: "The Young Women's Program for Cancer Care",
        },
        {
          href: '/services/cardiac-services-program/',
          text: 'Cardiac Services Program',
          ariaControls: 'uidfvrnt176t',
          id: 'uidqbp5frorx',
          children: [
            {
              href: '/services/cardiac-services-program/cardiac-surgery/',
              text: 'Cardiac surgery',
            },
            {
              href: '/services/cardiac-services-program/interventional-cardiology/',
              text: 'Interventional cardiology',
            },
            {
              href: '/services/cardiac-services-program/non-invasive-cardiac-testing/',
              text: 'Non-invasive cardiac testing',
            },
            {
              href: '/services/cardiac-services-program/structural-heart-program/',
              text: 'Structural heart program',
            },
          ],
        },
        {
          href: '/services/dental-care/',
          text: 'Dental Care',
        },
        {
          href: '/services/ear-nose-and-throat-otolaryngology/',
          text: 'Ear, nose and throat (otolaryngology)',
        },
        {
          href: '/services/emergency-room/',
          text: 'Emergency room',
        },
        {
          href: '/services/endocrinology/',
          text: 'Endocrinology',
        },
        {
          href: '/services/family-health-services/',
          text: 'Family health services',
        },
        {
          href: '/services/gastroenterology/',
          text: 'Gastroenterology',
          ariaControls: 'uidq76yr2a4y',
          id: 'uidi5smq9oqj',
          children: [
            {
              href: '/services/gastroenterology/colorectal-surgery/',
              text: 'Colorectal surgery',
            },
            {
              href: '/services/gastroenterology/gerd-and-upper-gi-surgeries/',
              text: 'GERD and upper GI surgeries',
            },
            {
              href: '/services/gastroenterology/hepatobiliary-surgery/',
              text: 'Hepatobiliary surgery',
            },
          ],
        },
        {
          href: '/services/hematology-and-oncology-services/',
          text: 'Hematology and oncology services',
        },
        {
          href: '/services/hernia-surgery/',
          text: 'Hernia surgery',
        },
        {
          href: '/services/hospitalists/',
          text: 'Hospitalists',
        },
        {
          href: '/services/imaging-services/',
          text: 'Imaging services',
        },
        {
          href: '/services/integrative-services/',
          ariaControls: 'uid4j6zlxes6',
          id: 'uidsk8fdxbi3',
          text: 'Integrative services',
          children: [
            {
              href: '/services/integrative-services/acupuncture/',
              text: 'Acupuncture',
            },
          ],
        },
        {
          href: '/services/interventional-radiology/',
          text: 'Interventional radiology',
        },
        {
          href: '/services/laboratory/',
          text: 'Laboratory',
        },
        {
          href: '/services/maternity-services/',
          text: 'Maternity services',
          ariaControls: 'uidvw9om58m8',
          id: 'uid1jqq973r5',
          children: [
            {
              href: '/services/maternity-services/breastfeeding-and-lactation-support/',
              text: 'Breastfeeding and lactation support',
            },
            {
              href: '/services/maternity-services/genetic-screenings/',
              text: 'Genetic screenings',
            },
            {
              href: '/services/maternity-services/maternal-fetal-medicine/',
              text: 'Maternal - Fetal Medicine',
            },
            {
              href: '/services/maternity-services/nicu/',
              text: 'NICU',
            },
            {
              href: '/services/maternity-services/prenatal-classNamees-and-education/',
              text: 'Prenatal classNamees and education',
            },
            {
              href: '/services/maternity-services/preparing-to-give-birth/',
              text: 'Preparing to give birth',
            },
            {
              href: '/services/maternity-services/visitation/',
              text: 'Visitation',
            },
            {
              href: '/services/maternity-services/what-to-expect-during-labor-delivery/',
              text: 'What to expect during labor & amp; delivery',
            },
          ],
        },
        {
          href: '/services/neurosciences/',
          text: 'Neurosciences',
          ariaControls: 'uidftcoy79mj',
          id: 'uid42sgcxkua',
          children: [
            {
              href: '/services/neurosciences/neurology/',
              text: 'Neurology',
              ariaControls: 'uidv3h7hpun1',
              id: 'uid3eghaheqs',
              children: [
                {
                  href: '/services/neurosciences/neurology/multiple-sclerosis-services/',
                  text: 'Multiple sclerosis services',
                },
                {
                  href: '/services/neurosciences/neurology/epilepsy/',
                  text: 'Epilepsy',
                },
              ],
            },
            {
              href: '/services/neurosciences/neurosurgery/',
              text: 'Neurosurgery',
            },
            {
              href: '/services/neurosciences/stroke-care/',
              text: 'Stroke care',
            },
          ],
        },
        {
          href: '/services/oral-maxillofacial-surgery/',
          text: 'Oral maxillofacial surgery',
        },
        {
          href: '/services/orthopedics/',
          text: 'Orthopedics',
          ariaControls: 'uidkude46e3e',
          id: 'uid5o9u8tq49',
          children: [
            {
              href: '/services/orthopedics/foot-and-ankle-surgery/',
              text: 'Foot and ankle surgery',
            },
            {
              href: '/services/orthopedics/hand-surgery/',
              text: 'Hand surgery',
            },
            {
              href: '/services/orthopedics/hip-surgery/',
              text: 'Hip surgery',
            },
            {
              href: '/services/orthopedics/knee-surgery/',
              text: 'Knee surgery',
            },
            {
              href: '/services/spine-surgery/',
              text: 'Spine surgery',
            },
            {
              href: '/services/sports-medicine/',
              text: 'Sports medicine',
            },
          ],
        },
        {
          href: '/services/pain-management/',
          text: 'Pain management',
        },
        {
          href: '/services/palliative-care/',
          text: 'Palliative care',
        },
        {
          href: '/services/pediatrics/',
          text: 'Pediatrics',
        },
        {
          href: '/services/physical-rehabilitation/',
          text: 'Physical rehabilitation',
        },
        {
          href: '/services/plastic-surgery/',
          text: 'Plastic surgery',
        },
        {
          href: '/services/sleep-center/',
          text: 'Sleep Center',
        },
        {
          href: '/services/spine-surgery/',
          text: 'Spine surgery',
        },
        {
          href: '/services/sports-medicine/',
          text: 'Sports medicine',
        },
        {
          href: '/services/surgical-services/',
          text: 'Surgical services',
          ariaControls: 'uide6z7aftuc',
          id: 'uid30x6joifp',
          children: [
            {
              href: '/services/surgical-services/ambulatory-surgery-center-at-harrison/',
              text: 'Ambulatory Surgery Center at Harrison',
            },
            {
              href: '/services/surgical-services/getting-ready-for-surgery/',
              text: 'Getting ready for surgery',
            },
            {
              href: '/services/surgical-services/robotic-surgery/',
              text: 'Robotic surgery',
            },
            {
              href: '/services/surgical-services/surgical-navigation-center/',
              text: 'Surgical Navigation Center',
            },
          ],
        },
        {
          href: '/services/thoracic-surgery/',
          text: 'Thoracic surgery',
        },
        {
          href: '/services/urgent-care/',
          text: 'Urgent care',
        },
        {
          href: '/services/urologic-surgery/',
          text: 'Urologic surgery',
        },
        {
          href: '/services/vascular-surgery/',
          text: 'Vascular surgery',
        },
        {
          href: '/services/virtual-health-care/',
          text: 'Virtual health care',
        },
        {
          href: '/services/womens-health/',
          text: "Women's health",
          ariaControls: 'uidwpkdusm95',
          id: 'uidbgpvpifks',
          children: [
            {
              href: '/services/womens-health/breast-health/',
              text: 'Breast health',
            },
            {
              href: '/services/womens-health/gynecologic-surgery/',
              text: 'Gynecologic surgery',
            },
            {
              href: '/services/womens-health/womens-imaging/',
              text: "Women's imaging",
            },
          ],
        },
        {
          href: '/services/wound-and-ostomy-care/',
          text: 'Wound and ostomy care',
          ariaControls: 'uidb3quo74ye',
          id: 'uide52kaouy4',
          children: [
            {
              href: '/services/wound-and-ostomy-care/hyperbaric-oxygen-therapy/',
              text: 'Hyperbaric oxygen therapy',
            },
            {
              href: '/services/wound-and-ostomy-care/limb-preservation-and-restoration-program/',
              text: 'Limb preservation and restoration program',
            },
          ],
        },
      ],
    },
    {
      text: 'Health Resources',
      href: 'javascript:;',
      id: 'uidt6mkie53e',
      ariaControls: 'uid445e2rfqa',
      label: 'Show submenu for Health Resources',
      className: 'parent js-dropdown-toggle',
      children: [
        {
          text: 'Calendar of Events',
          href: '/calendar/',
        },
        {
          text: 'Community Health & Wellness',
          href: '/health-resources/community-health-wellness/',
        },
        {
          text: 'Healthy Community',
          href: '/health-resources/healthy-community/',
        },
        {
          text: 'Health Matters Blog',
          href: 'https://healthmatters.wphospital.org/blogs?s=&amp;search_category=&amp;content_type=post',
          isExternal: true,
        },
        {
          text: 'Health Matters Magazine',
          href: 'https://healthmatters.wphospital.org/',
        },
        {
          text: 'Health Risk Assessments',
          href: 'https://s3.wphospital.org/healthmatters/index.html',
        },
        {
          text: 'Podcasts',
          href: 'https://healthmatters.wphospital.org/podcasts?s=&amp;search_category=&amp;content_type=podcasts',
          isExternal: true,
        },
        {
          text: 'Stay Connected With WPH',
          href: '/health-resources/stay-connected-with-wph/',
        },
        {
          text: 'UptoDate® Patient Education',
          href: '/health-resources/uptodate-patient-education/',
        },
        {
          text: 'Videos',
          href: 'https://healthmatters.wphospital.org/videos?s=&amp;search_category=&amp;content_type=videos',
          isExternal: true,
        },
      ],
    },
    {
      text: 'Locations',
      href: '/locations/',
    },
    {
      text: 'Contact Us',
      href: '/forms/contact-us/',
    },
  ];
};
